import * as React from 'react';
import { cn } from '../lib/cn';

export interface UniversalEmptyStateProps {
  /** Optional icon — Lucide icon component, rendered at h-10 w-10. */
  icon?: React.ComponentType<{ className?: string }>;
  title: string;
  description?: string;
  /** Right-side or below-text action (typically a primary Button). */
  action?: React.ReactNode;
  /** Compact variant — smaller padding for in-table empty states. */
  compact?: boolean;
  className?: string;
}

/**
 * UniversalEmptyState — empty list, no-search-results, no-data-yet illustration row.
 *
 * Use inside UniversalTable / UniversalDataTable's empty slot, or as a standalone
 * page-level empty state when a section has no records yet.
 */
export function UniversalEmptyState({
  icon: Icon,
  title,
  description,
  action,
  compact,
  className,
}: UniversalEmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center text-center',
        compact ? 'gap-2 p-6' : 'gap-3 p-12',
        className
      )}
    >
      {Icon && (
        <div className={cn('rounded-md bg-muted p-3 text-muted-foreground', compact && 'p-2')}>
          <Icon className={compact ? 'h-6 w-6' : 'h-10 w-10'} />
        </div>
      )}
      <div className="space-y-1">
        <h3 className="text-base font-medium text-foreground">{title}</h3>
        {description && (
          <p className="text-sm text-muted-foreground">{description}</p>
        )}
      </div>
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}
